import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ArrowUpRight, BookOpen, Building2, UsersRound } from 'lucide-react';
import { pedirPreguntasDeInicio, type PreguntaDeInicio } from '../../../common/services/api.js';
import { estudiosConDatos, pedirResumen } from '../../../common/services/catalogo.js';
import { Caja } from '../../../common/components/Caja.js';
import { type Idioma } from '../../../i18n/index.js';
import { pedirMapa, type MapaDeTemas as Mapa } from '../mapa/datos.js';
import { MapaDeTemas } from '../mapa/MapaDeTemas.js';
import { useSesion } from '../../../common/hooks/useSesion.js';
import { UltimosEstudios } from '../components/UltimosEstudios.js';
import { estudiosEnRedondo } from '../../../common/utils/redondeo.js';

const TEXTOS = {
  es: {
    titulo: 'Tu fuente de información sobre ',
    tituloFuente: 'la industria de los eventos',
    subtitulo: 'Athena analiza los estudios del sector para ayudarte a seguir las tendencias y encontrar datos para tu trabajo.',
    buscar: 'Buscar',
    pregunta: '¿Qué quieres saber del sector?',
    conectar: 'Usa Athena desde ChatGPT o Claude',
    chatgpt: 'Conectar con ChatGPT',
    claude: 'Conectar con Claude',
    cuenta: 'Crea tu cuenta para conectarla.',
    conCuenta: 'Consulta las instrucciones en Mi cuenta.',
    estudios: '{n} del sector, en un solo sitio.',
    prueba: 'Ejemplos',
    comprobar: 'Datos que puedes comprobar antes de citar',
    confianza: [
      { titulo: 'Ve a la fuente', texto: 'El estudio original y la página detrás de cada cifra.', icono: BookOpen },
      { titulo: 'Conoce quién está detrás', texto: 'Quién lo publica y quién lo financia, cuando consta.', icono: Building2 },
      {
        titulo: 'Entiende qué mide',
        texto: 'Muestra, fechas y método, según lo que declara el estudio.',
        icono: UsersRound,
      },
    ],
    explorar: 'Explorar los estudios',
  },
  en: {
    titulo: 'Your source of information on ',
    tituloFuente: 'the events industry',
    subtitulo: 'Athena analyses industry studies to help you follow trends and find data for your work.',
    buscar: 'Search',
    pregunta: 'What would you like to know about the industry?',
    conectar: 'Use Athena from ChatGPT or Claude',
    chatgpt: 'Connect with ChatGPT',
    claude: 'Connect with Claude',
    cuenta: 'Create your account to connect.',
    conCuenta: 'See the instructions in My account.',
    estudios: '{n} from the industry, in one place.',
    prueba: 'Examples',
    comprobar: 'Data you can check before you cite',
    confianza: [
      { titulo: 'Go to the source', texto: 'The original study and the page behind each figure.', icono: BookOpen },
      { titulo: 'Know who is behind it', texto: 'Who publishes it and who funds it, where available.', icono: Building2 },
      {
        titulo: 'Understand what it measures',
        texto: 'Sample, dates and method, as reported by the study.',
        icono: UsersRound,
      },
    ],
    explorar: 'Explore the studies',
  },
} as const;

/**
 * La portada: la caja y las preguntas prediseñadas. El modo lo elige Athena (MODO-01).
 *
 * **Escribir aquí no llama a ningún modelo**: lleva a la pantalla de consulta, que es la que envía.
 * Y pulsar una prediseñada abre su respuesta ya calculada (`plan/PLAN.md` § 4.9), sin gastar nada.
 * El número de estudios sale del catálogo (`GET /corpus/resumen`): escrito a mano envejece el día
 * que entra uno nuevo.
 */
export function InicioPage({ idioma }: { readonly idioma: Idioma }) {
  const t = TEXTOS[idioma];
  const navegar = useNavigate();
  const sesion = useSesion();
  const enlaceConector = (asistente: string) => {
    const destino = `/mi-cuenta?asistente=${asistente}`;
    return sesion ? destino : `/registro?volver=${encodeURIComponent(destino)}`;
  };
  const [estudios, setEstudios] = useState<number | null>(null);
  const [preguntas, setPreguntas] = useState<readonly PreguntaDeInicio[]>([]);
  const [mapa, setMapa] = useState<Mapa | null>(null);
  // Una pregunta del mapa se lleva a la caja: la caja se monta de nuevo con ese texto (`vez` cambia la clave).
  const [relleno, setRelleno] = useState({ texto: '', vez: 0 });
  const zonaDeLaCaja = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void pedirResumen().then(r => {
      setEstudios(r === null ? null : estudiosConDatos(r));
    });
  }, []);

  useEffect(() => {
    void pedirPreguntasDeInicio(idioma).then(setPreguntas);
  }, [idioma]);

  // Sin mapa (falta el fichero o no cumple el contrato) la portada es la misma de siempre.
  useEffect(() => {
    void pedirMapa().then(setMapa);
  }, []);

  useEffect(() => {
    if (relleno.vez === 0) return;
    const caja = zonaDeLaCaja.current?.querySelector('textarea');
    if (caja === null || caja === undefined) return;
    const quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    zonaDeLaCaja.current?.scrollIntoView({ behavior: quieto ? 'auto' : 'smooth', block: 'center' });
    caja.focus({ preventScroll: true });
    caja.setSelectionRange(caja.value.length, caja.value.length);
  }, [relleno]);

  return (
    <div className='flex grow flex-col'>
      <main className='flex grow flex-col items-center px-4 pt-[8vh] pb-20 sm:px-10'>
        <h1 className='m-0 max-w-[980px] text-center font-(family-name:--serif) text-[40px] leading-[1.06] font-bold tracking-[-0.02em] sm:text-[58px]'>
          {t.titulo}
          <span className='text-(--teal-deep)'>{t.tituloFuente}</span>.
        </h1>

        <p className='mt-[18px] mb-0 max-w-[620px] text-center font-(family-name:--text) text-[21px] leading-[1.5] text-(--ink-soft)'>
          {t.subtitulo}
        </p>

        {estudios === null ? null : (
          <p className='mt-3 mb-0 text-center font-(family-name:--text) text-[16px] text-(--ink-faint)'>
            {t.estudios.replace('{n}', estudiosEnRedondo(estudios, idioma))}
          </p>
        )}

        <div className='mt-8 grid w-full max-w-[1160px] gap-5 md:grid-cols-[minmax(0,3fr)_minmax(250px,1fr)]'>
          <div
            ref={zonaDeLaCaja}
            className='inicio-panel flex min-w-0 flex-col rounded-xl border border-(--line-strong) [&>form]:max-w-none [&>form]:grow [&>form>div:first-child]:grow [&>form>div:first-child]:rounded-none [&>form>div:first-child]:border-0 [&>form>div:first-child]:bg-transparent md:[&_textarea]:grow'
          >
            <Caja
              key={relleno.vez}
              textoInicial={relleno.texto}
              idioma={idioma}
              modo='auto'
              placeholder={t.pregunta}
              textoEnviar={t.buscar}
              enCurso={false}
              fallo={null}
              // Esta caja nunca manda la pregunta de verdad, solo navega a /consulta con el texto:
              // el captcha se resuelve allí, donde sale el POST (CAPTCHA-01, 2026-09-28, pedirlo dos
              // veces era justo lo que Iván notó al probarlo).
              conCaptcha={false}
              onEnviar={texto => {
                void navegar('/consulta', { state: { texto, enviar: true } });
              }}
            />
            {preguntas.length === 0 ? null : (
              <nav aria-label={t.prueba} className='mx-5 border-t border-(--line) py-4'>
                <ul className='m-0 flex list-none flex-wrap gap-3 p-0'>
                  {preguntas.map(p => (
                    <li key={p.id} className='min-w-[180px] flex-1'>
                      <Link
                        to={`/${p.modo}/${p.respuestaId}`}
                        state={{ texto: p.texto }}
                        className='block rounded-lg px-2 py-1 font-(family-name:--text) text-[15px] leading-[1.45] text-(--ink-soft) hover:bg-(--paper) hover:text-(--teal-deep) focus-visible:outline-2 focus-visible:outline-(--teal-deep)'
                      >
                        {p.texto}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
          <aside
            aria-labelledby='conectar-inicio'
            className='inicio-panel flex flex-col justify-center gap-3 rounded-xl border border-(--line-strong) p-5'
          >
            <h2 id='conectar-inicio' className='m-0 font-(family-name:--text) text-[19px] leading-[1.4] font-semibold text-(--ink)'>
              {t.conectar}
            </h2>
            <div className='flex flex-col gap-2'>
              <Link
                to={enlaceConector('chatgpt')}
                className='rounded-full border border-(--teal-deep) px-3 py-2 text-center font-(family-name:--text) text-[16px] text-(--teal-deep) hover:bg-(--paper)'
              >
                {t.chatgpt}
              </Link>
              <Link
                to={enlaceConector('claude')}
                className='rounded-full border border-(--teal-deep) px-3 py-2 text-center font-(family-name:--text) text-[16px] text-(--teal-deep) hover:bg-(--paper)'
              >
                {t.claude}
              </Link>
            </div>
            <p className='m-0 font-(family-name:--text) text-[15px] leading-[1.4] text-(--ink-faint)'>{sesion ? t.conCuenta : t.cuenta}</p>
          </aside>
        </div>

        <section
          aria-labelledby='comprobar-inicio'
          className='mt-8 w-full max-w-[1160px] rounded-xl bg-[color-mix(in_srgb,var(--teal-deep)_5%,var(--paper))] px-5 py-6 sm:px-7'
        >
          <div className='flex flex-wrap items-center justify-between gap-x-6 gap-y-3'>
            <h2
              id='comprobar-inicio'
              className='m-0 max-w-[580px] font-(family-name:--serif) text-[24px] leading-[1.25] font-semibold tracking-[-0.015em] text-(--ink)'
            >
              {t.comprobar}
            </h2>
            <Link
              to='/fuentes'
              className='group inline-flex shrink-0 items-center gap-1.5 rounded-sm font-(family-name:--text) text-[14px] font-medium text-(--teal-deep) hover:underline hover:underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-(--teal-deep)'
            >
              {t.explorar}
              <ArrowUpRight aria-hidden='true' size={17} strokeWidth={1.6} />
            </Link>
          </div>
          <div className='mt-5 grid gap-5 border-t border-(--line) pt-5 md:grid-cols-3 md:gap-0'>
            {t.confianza.map(({ titulo, texto, icono: Icono }) => (
              <div key={titulo} className='flex gap-3 md:px-6 md:first:pl-0 md:last:pr-0 md:not-first:border-l md:not-first:border-(--line)'>
                <Icono aria-hidden='true' size={22} strokeWidth={1.5} className='mt-0.5 shrink-0 text-(--teal-deep)' />
                <div>
                  <h3 className='m-0 font-(family-name:--text) text-[16px] leading-[1.4] font-semibold text-(--ink)'>{titulo}</h3>
                  <p className='mt-1.5 mb-0 font-(family-name:--text) text-[15px] leading-[1.5] text-(--ink-soft)'>{texto}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {mapa === null ? null : (
          <MapaDeTemas
            mapa={mapa}
            idioma={idioma}
            onElegirPregunta={texto => {
              setRelleno(r => ({ texto, vez: r.vez + 1 }));
            }}
          />
        )}
        <UltimosEstudios idioma={idioma} />
      </main>
    </div>
  );
}
